import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  CogIcon,
  KeyIcon,
  ShieldCheckIcon,
  BellIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  PlusIcon,
  ChartBarIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';
import { exchangeApi, userApi } from '@/services/api';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import Modal from '@/components/UI/Modal';
import ConfirmDialog from '@/components/UI/ConfirmDialog';
import useThemeStore, { Theme } from '@/store/themeStore';

interface ApiKey {
  id: string;
  exchangeName: string;
  isTestnet: boolean;
  isActive: boolean;
  createdAt: string;
  lastUsed?: string;
}

interface UserSettings {
  notifications: {
    email: boolean;
    push: boolean;
    trading: boolean;
    alerts: boolean;
  };
  trading: {
    defaultRiskLevel: 'low' | 'medium' | 'high';
    maxPositionSize: number;
    autoStopLoss: boolean;
    defaultStopLossPercentage: number;
  };
  display: {
    theme: 'light' | 'dark';
    currency: 'USD' | 'EUR' | 'BTC';
    timezone: string;
  };
  externalData: {
    enableFearGreed: boolean;
    enableNewsSentiment: boolean;
    enableSocialSentiment: boolean;
  };
}

const SettingsPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');

  // Theme store integration
  const { setTheme } = useThemeStore();

  // API Key form state
  const [newApiKey, setNewApiKey] = useState({
    exchangeName: 'binance',
    apiKey: '',
    secretKey: '',
    passphrase: '',
    isTestnet: true
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [apiKeysRes, profileRes] = await Promise.allSettled([
        exchangeApi.getApiKeys(),
        userApi.getProfile()
      ]);

      if (apiKeysRes.status === 'fulfilled') {
        // Handle nested data structure and ensure we always get an array
        const responseData: any = apiKeysRes.value?.data;
        const apiKeysArray = Array.isArray(responseData) 
          ? responseData 
          : Array.isArray(responseData?.data) 
            ? responseData.data 
            : Array.isArray(responseData?.apiKeys)
              ? responseData.apiKeys
              : [];
        setApiKeys(apiKeysArray);
      }

      // Load real settings from backend
      if (profileRes.status === 'fulfilled') {
        const profileData = profileRes.value as any;
        // API response structure: { data: { user: { settings: {...} } } }
        const userSettings = profileData.data?.user?.settings || profileData.user?.settings;
        
        setSettings(userSettings || {
          notifications: {
            email: true,
            push: true,
            trading: true,
            alerts: true
          },
          trading: {
            defaultRiskLevel: 'medium',
            maxPositionSize: 0.1,
            autoStopLoss: true,
            defaultStopLossPercentage: 5
          },
          display: {
            theme: 'light',
            currency: 'USD',
            timezone: 'UTC'
          },
          externalData: {
            enableFearGreed: false,
            enableNewsSentiment: false,
            enableSocialSentiment: false
          }
        });
        
        console.log('[Settings] Loaded user settings:', userSettings);
      }

    } catch (error: any) {
      console.error('Settings fetch error:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Save settings to backend
      await userApi.updateSettings(settings);
      
      toast.success('Settings saved successfully');
    } catch (error: any) {
      console.error('Save settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddApiKey = async () => {
    try {
      await exchangeApi.createApiKey({
        exchange: newApiKey.exchangeName,
        apiKey: newApiKey.apiKey,
        secretKey: newApiKey.secretKey,
        passphrase: newApiKey.passphrase || undefined,
        isTestnet: newApiKey.isTestnet
      });

      toast.success('API key added successfully');
      setShowAddKeyModal(false);
      setNewApiKey({
        exchangeName: 'binance',
        apiKey: '',
        secretKey: '',
        passphrase: '',
        isTestnet: true
      });
      
      await loadData();
      
    } catch (error: any) {
      console.error('Add API key error:', error);
      toast.error(error.response?.data?.message || 'Failed to add API key');
    }
  };

  const handleDeleteApiKey = async (keyId: string) => {
    try {
      await exchangeApi.deleteApiKey(keyId);
      toast.success('API key deleted successfully');
      setShowDeleteDialog(null);
      await loadData();
    } catch (error: any) {
      console.error('Delete API key error:', error);
      toast.error('Failed to delete API key');
    }
  };

  const handleTestConnection = async (exchangeName: string) => {
    try {
      await exchangeApi.testConnection({ exchange: exchangeName });
      toast.success('Connection test successful');
    } catch (error: any) {
      console.error('Test connection error:', error);
      toast.error('Connection test failed');
    }
  };

  const updateSettings = (section: keyof UserSettings, key: string, value: any) => {
    if (!settings) return;
    
    // Update theme store when theme setting changes
    if (section === 'display' && key === 'theme') {
      setTheme(value as Theme);
    }
    
    setSettings(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [key]: value
      }
    }));
  };

  const tabs = [
    { id: 'general', name: 'General', icon: CogIcon },
    { id: 'api-keys', name: 'API Keys', icon: KeyIcon },
    { id: 'market-data', name: 'Market Data', icon: ChartBarIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-custom flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-custom">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-heading-1 text-primary-custom">Settings</h1>
          <p className="mt-2 text-body text-secondary-custom">
            Manage your trading platform preferences and configuration.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:space-x-8">
          {/* Sidebar */}
          <div className="lg:w-64 mb-8 lg:mb-0">
            <nav className="card">
              <ul className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <li key={tab.id}>
                      <button
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center px-3 py-3 text-body font-medium rounded-lg transition-all duration-200 ${
                          activeTab === tab.id
                            ? 'bg-blue-50 text-blue-700 shadow-sm'
                            : 'text-secondary-custom hover:text-primary-custom hover:bg-secondary-custom'
                        }`}
                      >
                        <Icon className={`mr-3 h-5 w-5 ${
                          activeTab === tab.id ? 'text-blue-500' : 'text-tertiary-custom'
                        }`} />
                        {tab.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="card">
              {activeTab === 'general' && (
                <div>
                  <h2 className="text-heading-2 text-primary-custom mb-6">General Settings</h2>
                  
                  {/* Trading Settings */}
                  <div className="mb-8">
                    <h3 className="text-md font-medium text-gray-900 mb-4">Trading</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Risk Level
                        </label>
                        <select
                          value={settings?.trading.defaultRiskLevel}
                          onChange={(e) => updateSettings('trading', 'defaultRiskLevel', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Position Size (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={(settings?.trading.maxPositionSize || 0) * 100}
                          onChange={(e) => updateSettings('trading', 'maxPositionSize', parseFloat(e.target.value) / 100)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings?.trading.autoStopLoss}
                          onChange={(e) => updateSettings('trading', 'autoStopLoss', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Auto Stop Loss
                        </label>
                      </div>
                      
                      {settings?.trading.autoStopLoss && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Stop Loss (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="0.1"
                            value={settings?.trading.defaultStopLossPercentage}
                            onChange={(e) => updateSettings('trading', 'defaultStopLossPercentage', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Display Settings */}
                  <div className="mb-8">
                    <h3 className="text-heading-3 text-primary-custom mb-6">Display Preferences</h3>
                    
                    {/* Theme Selection */}
                    <div className="mb-6">
                      <label className="form-label mb-3">
                        Theme
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          onClick={() => updateSettings('display', 'theme', 'light')}
                          className={`card p-4 text-left transition-all duration-200 ${
                            settings?.display.theme === 'light'
                              ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
                              : 'hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              settings?.display.theme === 'light' 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <SunIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-body font-medium text-primary-custom">Light Theme</div>
                              <div className="text-caption text-secondary-custom">Professional and clean</div>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => updateSettings('display', 'theme', 'dark')}
                          className={`card p-4 text-left transition-all duration-200 ${
                            settings?.display.theme === 'dark'
                              ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200'
                              : 'hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              settings?.display.theme === 'dark' 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              <MoonIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-body font-medium text-primary-custom">Dark Theme</div>
                              <div className="text-caption text-secondary-custom">Easier on the eyes</div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Currency Selection */}
                    <div className="mb-6">
                      <label className="form-label mb-2">
                        Default Currency
                      </label>
                      <select
                        value={settings?.display.currency}
                        onChange={(e) => updateSettings('display', 'currency', e.target.value)}
                        className="form-input"
                      >
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="BTC">BTC - Bitcoin</option>
                      </select>
                    </div>

                    {/* Timezone */}
                    <div>
                      <label className="form-label mb-2">
                        Timezone
                      </label>
                      <select
                        value={settings?.display.timezone}
                        onChange={(e) => updateSettings('display', 'timezone', e.target.value)}
                        className="form-input"
                      >
                        <option value="UTC">UTC - Coordinated Universal Time</option>
                        <option value="America/New_York">EST - Eastern Time</option>
                        <option value="America/Los_Angeles">PST - Pacific Time</option>
                        <option value="Europe/London">GMT - Greenwich Mean Time</option>
                        <option value="Europe/Paris">CET - Central European Time</option>
                        <option value="Asia/Tokyo">JST - Japan Standard Time</option>
                        <option value="Asia/Shanghai">CST - China Standard Time</option>
                      </select>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSettings}
                      disabled={saving}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'market-data' && (
                <div className="p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-6">Market Data & Sentiment</h2>
                  
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-6">
                      Enable external market data sources to enhance your trading strategies with sentiment analysis.
                      These settings control whether your strategies will use external data in their decision-making process.
                    </p>
                  </div>

                  {/* Fear & Greed Index */}
                  <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id="enable-fear-greed"
                            checked={settings?.externalData.enableFearGreed}
                            onChange={(e) => updateSettings('externalData', 'enableFearGreed', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="enable-fear-greed" className="ml-3 text-sm font-medium text-gray-900">
                            Fear & Greed Index
                          </label>
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Free
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 ml-7">
                          Use the Crypto Fear & Greed Index to adjust trading strategies based on market sentiment. 
                          Extreme fear may indicate buying opportunities, while extreme greed suggests caution.
                        </p>
                        <div className="mt-2 ml-7">
                          <p className="text-xs text-gray-500">
                            <strong>Data Source:</strong> Alternative.me • <strong>Update Frequency:</strong> Daily
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* News Sentiment */}
                  <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id="enable-news-sentiment"
                            checked={settings?.externalData.enableNewsSentiment}
                            onChange={(e) => updateSettings('externalData', 'enableNewsSentiment', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={saving}
                          />
                          <label htmlFor="enable-news-sentiment" className="ml-3 text-sm font-medium text-gray-900">
                            News Sentiment Analysis
                          </label>
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 ml-7">
                          Analyze news sentiment from cryptocurrency news sources to gauge market mood. 
                          Uses multiple news APIs to provide comprehensive sentiment analysis for your trading pairs.
                        </p>
                        <div className="ml-7 mt-2">
                          <p className="text-xs text-gray-500">
                            Supported sources: NewsAPI.org, Alpha Vantage, CryptoPanic
                          </p>
                          <p className="text-xs text-gray-500">
                            Collection frequency: Every 6 hours for active trading pairs
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Social Sentiment */}
                  <div className="mb-8 p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <input
                            type="checkbox"
                            id="enable-social-sentiment"
                            checked={settings?.externalData.enableSocialSentiment}
                            onChange={(e) => updateSettings('externalData', 'enableSocialSentiment', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            disabled={true}
                          />
                          <label htmlFor="enable-social-sentiment" className="ml-3 text-sm font-medium text-gray-400">
                            Social Media Sentiment
                          </label>
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Coming Soon
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 ml-7">
                          Track social media sentiment from Twitter, Reddit, and other platforms to understand 
                          community sentiment around cryptocurrencies. This feature will be available in a future update.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Impact Information */}
                  {settings?.externalData.enableFearGreed && (
                    <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">How Fear & Greed Affects Your Strategies</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• <strong>Extreme Fear (0-25):</strong> Increases buying confidence and position sizes</li>
                        <li>• <strong>Fear (25-49):</strong> Moderately increases buying opportunities</li>
                        <li>• <strong>Greed (51-75):</strong> Applies moderate caution to buying decisions</li>
                        <li>• <strong>Extreme Greed (76-100):</strong> Reduces buying and increases selling confidence</li>
                      </ul>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSettings}
                      disabled={saving}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Market Data Settings'}
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'api-keys' && (
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">API Keys</h3>
                      <button
                        onClick={() => setShowAddKeyModal(true)}
                        className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add API Key
                      </button>
                    </div>
                  </div>
                  
                  {apiKeys.length === 0 ? (
                    <div className="text-center py-8">
                      <KeyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No API keys configured</h3>
                      <p className="text-gray-600 mb-4">
                        Add exchange API keys to enable live trading functionality
                      </p>
                      <button
                        onClick={() => setShowAddKeyModal(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
                      >
                        Add Your First API Key
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Exchange
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Created
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Last Used
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {apiKeys.map((apiKey) => (
                            <tr key={apiKey.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900 capitalize">
                                  {apiKey.exchangeName}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  apiKey.isTestnet ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {apiKey.isTestnet ? 'Testnet' : 'Live'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  apiKey.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {apiKey.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(apiKey.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {apiKey.lastUsed ? new Date(apiKey.lastUsed).toLocaleDateString() : 'Never'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleTestConnection(apiKey.exchangeName)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    Test
                                  </button>
                                  <button
                                    onClick={() => setShowDeleteDialog(apiKey.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'notifications' && settings && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Notification Settings</h3>
                  
                  <div className="space-y-4">
                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Email Notifications</span>
                        <p className="text-xs text-gray-500">Receive important updates via email</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.email}
                        onChange={(e) => updateSettings('notifications', 'email', e.target.checked)}
                        className="rounded"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Push Notifications</span>
                        <p className="text-xs text-gray-500">Browser push notifications</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.push}
                        onChange={(e) => updateSettings('notifications', 'push', e.target.checked)}
                        className="rounded"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Trading Alerts</span>
                        <p className="text-xs text-gray-500">Notifications about trades and strategy execution</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.trading}
                        onChange={(e) => updateSettings('notifications', 'trading', e.target.checked)}
                        className="rounded"
                      />
                    </label>
                    
                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Price Alerts</span>
                        <p className="text-xs text-gray-500">Notifications when prices hit your targets</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings.notifications.alerts}
                        onChange={(e) => updateSettings('notifications', 'alerts', e.target.checked)}
                        className="rounded"
                      />
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Security Settings</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">Password</h4>
                      <button className="text-indigo-600 hover:text-indigo-800 text-sm">
                        Change Password
                      </button>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">Two-Factor Authentication</h4>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                        </div>
                        <button className="text-indigo-600 hover:text-indigo-800 text-sm">
                          Enable 2FA
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-base font-medium text-gray-900 mb-4">API Access</h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input type="checkbox" className="mr-2 rounded" defaultChecked />
                          <span className="text-sm text-gray-700">Enable API access</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" className="mr-2 rounded" defaultChecked />
                          <span className="text-sm text-gray-700">Require IP whitelist for API calls</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add API Key Modal */}
      <Modal
        isOpen={showAddKeyModal}
        onClose={() => setShowAddKeyModal(false)}
        title="Add API Key"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exchange
            </label>
            <select
              value={newApiKey.exchangeName}
              onChange={(e) => setNewApiKey(prev => ({ ...prev, exchangeName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="binance">Binance</option>
              <option value="coinbase">Coinbase Pro</option>
              <option value="kraken">Kraken</option>
              <option value="bybit">Bybit</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={newApiKey.apiKey}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, apiKey: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your API key"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showApiKey ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secret Key
            </label>
            <div className="relative">
              <input
                type={showSecretKey ? 'text' : 'password'}
                value={newApiKey.secretKey}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, secretKey: e.target.value }))}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your secret key"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showSecretKey ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
          
          {newApiKey.exchangeName === 'coinbase' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passphrase (Coinbase Pro)
              </label>
              <input
                type="password"
                value={newApiKey.passphrase}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, passphrase: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter your passphrase"
              />
            </div>
          )}
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newApiKey.isTestnet}
                onChange={(e) => setNewApiKey(prev => ({ ...prev, isTestnet: e.target.checked }))}
                className="mr-2 rounded"
              />
              <span className="text-sm text-gray-700">Use testnet/sandbox environment</span>
            </label>
            
            {/* Kraken testnet warning */}
            {newApiKey.exchangeName === 'kraken' && newApiKey.isTestnet && (
              <div className="mt-2 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-r-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Kraken does not provide a separate testnet environment. 
                      Your API key will be stored for development purposes but won't be validated. 
                      For live trading, uncheck the testnet option.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowAddKeyModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddApiKey}
              disabled={!newApiKey.apiKey || !newApiKey.secretKey}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              Add API Key
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete API Key Confirmation */}
      <ConfirmDialog
        isOpen={!!showDeleteDialog}
        onClose={() => setShowDeleteDialog(null)}
        onConfirm={() => showDeleteDialog && handleDeleteApiKey(showDeleteDialog)}
        title="Delete API Key"
        message="Are you sure you want to delete this API key? This action cannot be undone and will disable live trading for this exchange."
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default SettingsPage; 